import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const Login = () => {
    const { instance } = useMsal();
    const canvasContainerRef = useRef(null);
    const glassCardRef = useRef(null);
    const lightStateRef = useRef(null);
    const particleSystemRef = useRef(null);
    const [rendererError, setRendererError] = useState(null);
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const mix = (from, to, amount) => from + (to - from) * amount;

    const handleLogin = async () => {
        // Prevent multiple redirects
        if (instance.getActiveAccount()) return; // Already logged in?

        try {
            await instance.loginRedirect(loginRequest);
        } catch (e) {
            console.error("Login failed:", e);
        }
    };

    const handleLoginPointerMove = (event) => {
        const card = glassCardRef.current;
        if (!card) return;
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

        const rect = card.getBoundingClientRect();
        const centerLocalX = rect.width / 2;
        const centerLocalY = rect.height / 2;
        const rawDx = event.clientX - (rect.left + centerLocalX);
        const rawDy = event.clientY - (rect.top + centerLocalY);
        const vectorLength = Math.hypot(rawDx, rawDy);
        const dx = vectorLength ? rawDx / vectorLength : 0;
        const dy = vectorLength ? rawDy / vectorLength : -1;
        const scaleX = dx === 0 ? Number.POSITIVE_INFINITY : centerLocalX / Math.abs(dx);
        const scaleY = dy === 0 ? Number.POSITIVE_INFINITY : centerLocalY / Math.abs(dy);
        const scale = Math.min(scaleX, scaleY);
        const edgeX = centerLocalX + dx * scale;
        const edgeY = centerLocalY + dy * scale;

        const edgeScreenX = rect.left + edgeX;
        const edgeScreenY = rect.top + edgeY;
        const distance = Math.hypot(event.clientX - edgeScreenX, event.clientY - edgeScreenY);
        const distanceFalloff = clamp(1 - distance / (Math.max(rect.width, rect.height) * 1.55), 0, 1);
        const influence = distanceFalloff * distanceFalloff * (3 - 2 * distanceFalloff);
        const normalLength = Math.hypot(edgeX - centerLocalX, edgeY - centerLocalY) || 1;
        const normalX = (edgeX - centerLocalX) / normalLength;
        const normalY = (edgeY - centerLocalY) / normalLength;
        const tangentX = -normalY;
        const tangentY = normalX;
        const verticalness = Math.abs(normalX);
        const dispersion = 16 + influence * 16;
        const bandWidth = mix(Math.max(300, rect.width * 1.18), 120, verticalness);
        const bandHeight = mix(120, Math.max(380, rect.height * 1.45), verticalness);
        const washWidth = mix(Math.max(380, rect.width * 1.38), 168, verticalness);
        const washHeight = mix(168, Math.max(520, rect.height * 1.82), verticalness);
        const cyanX = clamp(edgeX - tangentX * dispersion, 0, rect.width);
        const cyanY = clamp(edgeY - tangentY * dispersion, 0, rect.height);
        const magentaX = clamp(edgeX + tangentX * dispersion, 0, rect.width);
        const magentaY = clamp(edgeY + tangentY * dispersion, 0, rect.height);
        const prismOpacity = 0.42 + influence * 0.54;
        const prismWhite = 0.06 + influence * 0.36;
        const prismCyan = 0.04 + influence * 0.3;
        const prismMagenta = 0.14 + influence * 0.72;
        const prismViolet = 0.11 + influence * 0.64;
        const prismSaturation = 1.04 + influence * 0.08;
        const prismBrightness = 1.015 + influence * 0.035;
        const targetLight = {
            edgeX,
            edgeY,
            cyanX,
            cyanY,
            magentaX,
            magentaY,
            bandWidth,
            bandHeight,
            washWidth,
            washHeight,
            prismOpacity,
            prismWhite,
            prismCyan,
            prismMagenta,
            prismViolet,
            prismSaturation,
            prismBrightness
        };
        const previousLight = lightStateRef.current ?? targetLight;
        const smoothedLight = {
            edgeX: mix(previousLight.edgeX, targetLight.edgeX, 0.22),
            edgeY: mix(previousLight.edgeY, targetLight.edgeY, 0.22),
            cyanX: mix(previousLight.cyanX, targetLight.cyanX, 0.22),
            cyanY: mix(previousLight.cyanY, targetLight.cyanY, 0.22),
            magentaX: mix(previousLight.magentaX, targetLight.magentaX, 0.22),
            magentaY: mix(previousLight.magentaY, targetLight.magentaY, 0.22),
            bandWidth: mix(previousLight.bandWidth, targetLight.bandWidth, 0.22),
            bandHeight: mix(previousLight.bandHeight, targetLight.bandHeight, 0.22),
            washWidth: mix(previousLight.washWidth, targetLight.washWidth, 0.22),
            washHeight: mix(previousLight.washHeight, targetLight.washHeight, 0.22),
            prismOpacity: mix(previousLight.prismOpacity, targetLight.prismOpacity, 0.22),
            prismWhite: mix(previousLight.prismWhite, targetLight.prismWhite, 0.22),
            prismCyan: mix(previousLight.prismCyan, targetLight.prismCyan, 0.22),
            prismMagenta: mix(previousLight.prismMagenta, targetLight.prismMagenta, 0.22),
            prismViolet: mix(previousLight.prismViolet, targetLight.prismViolet, 0.22),
            prismSaturation: mix(previousLight.prismSaturation, targetLight.prismSaturation, 0.22),
            prismBrightness: mix(previousLight.prismBrightness, targetLight.prismBrightness, 0.22)
        };
        lightStateRef.current = smoothedLight;

        card.style.setProperty('--light-x', `${smoothedLight.edgeX.toFixed(2)}px`);
        card.style.setProperty('--light-y', `${smoothedLight.edgeY.toFixed(2)}px`);
        card.style.setProperty('--cyan-x', `${smoothedLight.cyanX.toFixed(2)}px`);
        card.style.setProperty('--cyan-y', `${smoothedLight.cyanY.toFixed(2)}px`);
        card.style.setProperty('--magenta-x', `${smoothedLight.magentaX.toFixed(2)}px`);
        card.style.setProperty('--magenta-y', `${smoothedLight.magentaY.toFixed(2)}px`);
        card.style.setProperty('--band-width', `${smoothedLight.bandWidth.toFixed(2)}px`);
        card.style.setProperty('--band-height', `${smoothedLight.bandHeight.toFixed(2)}px`);
        card.style.setProperty('--wash-width', `${smoothedLight.washWidth.toFixed(2)}px`);
        card.style.setProperty('--wash-height', `${smoothedLight.washHeight.toFixed(2)}px`);
        card.style.setProperty('--prism-opacity', smoothedLight.prismOpacity.toFixed(2));
        card.style.setProperty('--prism-white', smoothedLight.prismWhite.toFixed(2));
        card.style.setProperty('--prism-cyan', smoothedLight.prismCyan.toFixed(2));
        card.style.setProperty('--prism-magenta', smoothedLight.prismMagenta.toFixed(2));
        card.style.setProperty('--prism-violet', smoothedLight.prismViolet.toFixed(2));
        card.style.setProperty('--prism-saturation', smoothedLight.prismSaturation.toFixed(3));
        card.style.setProperty('--prism-brightness', smoothedLight.prismBrightness.toFixed(3));
    };

    const handleLoginPointerLeave = () => {
        const card = glassCardRef.current;
        if (!card) return;
        lightStateRef.current = null;

        card.style.setProperty('--light-x', '50%');
        card.style.setProperty('--light-y', '0%');
        card.style.setProperty('--cyan-x', 'calc(50% - 12px)');
        card.style.setProperty('--cyan-y', '0%');
        card.style.setProperty('--magenta-x', 'calc(50% + 12px)');
        card.style.setProperty('--magenta-y', '0%');
        card.style.setProperty('--band-width', '300px');
        card.style.setProperty('--band-height', '120px');
        card.style.setProperty('--wash-width', '380px');
        card.style.setProperty('--wash-height', '168px');
        card.style.setProperty('--prism-opacity', '0.42');
        card.style.setProperty('--prism-white', '0.06');
        card.style.setProperty('--prism-cyan', '0.04');
        card.style.setProperty('--prism-magenta', '0.14');
        card.style.setProperty('--prism-violet', '0.11');
        card.style.setProperty('--prism-saturation', '1.02');
        card.style.setProperty('--prism-brightness', '1.01');
    };

    useEffect(() => {
        if (!canvasContainerRef.current) return;

        // --- THREE.JS PARTICLE SYSTEM IMPLEMENTATION ---
        class ParticleSystem {
            constructor(container) {
                this.container = container;
                this.scene = null;
                this.camera = null;
                this.renderer = null;
                this.particles = null;
                this.material = null;
                this.mousePos = { x: 0, y: 0 };
                this.targetMousePos = { x: 0, y: 0 };
                this.time = 0;
                this.animationId = null;

                this.init();
            }

            init() {
                const width = window.innerWidth;
                const height = window.innerHeight;

                this.scene = new THREE.Scene();
                this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
                this.camera.position.z = 50;

                try {
                    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
                    this.renderer.setSize(width, height);
                    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
                    this.renderer.setClearColor(0x000000, 0);
                } catch (e) {
                    setRendererError("Three.js disabled");
                    return;
                }

                this.container.innerHTML = '';
                this.container.appendChild(this.renderer.domElement);

                const particleCount = 800;
                const positions = new Float32Array(particleCount * 3);
                const attributes = new Float32Array(particleCount * 3); // size, opacity, speedY

                for (let i = 0; i < particleCount; i++) {
                    positions[i * 3] = (Math.random() - 0.5) * 200; // x
                    positions[i * 3 + 1] = (Math.random() - 0.5) * 200; // y

                    const layer = Math.random();
                    if (layer < 0.5) {
                        // Background: Microscopic
                        positions[i * 3 + 2] = -20 - Math.random() * 30; // z
                        attributes[i * 3] = 0.2 + Math.random() * 0.8; // size
                        attributes[i * 3 + 1] = 0.05 + Math.random() * 0.05; // opacity
                        attributes[i * 3 + 2] = 0.02 + Math.random() * 0.02; // speedY
                    } else {
                        // Midground: Small
                        positions[i * 3 + 2] = 0 + Math.random() * 20; // z
                        attributes[i * 3] = 1.0 + Math.random() * 1.0; // size
                        attributes[i * 3 + 1] = 0.05 + Math.random() * 0.05; // opacity
                        attributes[i * 3 + 2] = 0.04 + Math.random() * 0.03; // speedY
                    }
                }

                const geometry = new THREE.BufferGeometry();
                geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
                geometry.setAttribute('aSettings', new THREE.BufferAttribute(attributes, 3)); // x=size, y=opacity, z=speedY

                const vertexShader = `
                    attribute vec3 aSettings;
                    uniform float time;
                    varying float vOpacity;

                    void main() {
                        vec3 pos = position;

                        // Orbital Sway (Universe rotating)
                        float orbitSpeed = time * 0.15 * aSettings.z;
                        float c = cos(orbitSpeed);
                        float s = sin(orbitSpeed);
                        mat2 rot = mat2(c, -s, s, c);
                        pos.xy = rot * pos.xy;

                        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
                        gl_Position = projectionMatrix * mvPosition;

                        // Size attenuation based on depth
                        gl_PointSize = aSettings.x * (300.0 / -mvPosition.z);

                        // Opacity
                        vOpacity = aSettings.y;
                    }
                `;

                const fragmentShader = `
                    varying float vOpacity;
                    void main() {
                        vec2 coord = gl_PointCoord - vec2(0.5);
                        float dist = length(coord);
                        if (dist > 0.5) discard;

                        // Soft bokeh edge
                        float alpha = smoothstep(0.5, 0.1, dist) * vOpacity;

                        gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
                    }
                `;

                this.material = new THREE.ShaderMaterial({
                    vertexShader,
                    fragmentShader,
                    uniforms: {
                        time: { value: 0 }
                    },
                    transparent: true,
                    depthWrite: false,
                    blending: THREE.AdditiveBlending
                });

                this.particles = new THREE.Points(geometry, this.material);
                this.scene.add(this.particles);

                this.animate();
            }

            animate() {
                if (!this.renderer) return;
                this.animationId = requestAnimationFrame(() => this.animate());

                this.time += 0.016;
                this.material.uniforms.time.value = this.time;

                this.renderer.render(this.scene, this.camera);
            }

            onResize() {
                if (!this.camera || !this.renderer) return;
                const width = window.innerWidth;
                const height = window.innerHeight;
                this.camera.aspect = width / height;
                this.camera.updateProjectionMatrix();
                this.renderer.setSize(width, height);
            }

            dispose() {
                if (this.animationId) cancelAnimationFrame(this.animationId);
                if (this.renderer) {
                    this.renderer.dispose();
                    this.renderer.forceContextLoss();
                    this.renderer.domElement = null;
                }
                if (this.material) this.material.dispose();
                // Clean geometries
                if (this.particles && this.particles.geometry) this.particles.geometry.dispose();
            }
        }

        // --- INIT & EVENTS ---
        try {
            particleSystemRef.current = new ParticleSystem(canvasContainerRef.current);
            setRendererError(null); // Clear error if success
        } catch (e) {
            console.error(e);
            setRendererError("Failed to init particle system: " + e.message);
        }

        const handleResize = () => {
            if (particleSystemRef.current) particleSystemRef.current.onResize();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (particleSystemRef.current) particleSystemRef.current.dispose();
        };
    }, []);

    return (
        <div
            className="login-container font-sans text-white min-h-screen w-full relative flex items-center justify-center"
            onPointerMove={handleLoginPointerMove}
            onPointerLeave={handleLoginPointerLeave}
            style={{ background: 'radial-gradient(circle at top right, rgba(76, 29, 149, 0.15) 0%, transparent 40%), radial-gradient(circle at bottom left, rgba(49, 46, 129, 0.15) 0%, transparent 40%), #000000' }}
        >
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

                .login-container {
                    font-family: 'Inter', sans-serif;
                }

                @keyframes cardEntrance {
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes ambientDrift {
                    0%, 100% { opacity: 0.15; transform: scale(1); }
                    50% { opacity: 0.3; transform: scale(1.05); }
                }

                .directional-glint {
                    position: relative;
                    overflow: hidden;
                    isolation: isolate;
                    --light-x: 50%;
                    --light-y: 0%;
                    --cyan-x: calc(50% - 12px);
                    --cyan-y: 0%;
                    --magenta-x: calc(50% + 12px);
                    --magenta-y: 0%;
                    --band-width: 300px;
                    --band-height: 120px;
                    --wash-width: 380px;
                    --wash-height: 168px;
                    --prism-opacity: 0.42;
                    --prism-white: 0.06;
                    --prism-cyan: 0.04;
                    --prism-magenta: 0.14;
                    --prism-violet: 0.11;
                    --prism-saturation: 1.02;
                    --prism-brightness: 1.01;
                }

                .directional-glint::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: inherit;
                    padding: 1.5px;
                    background:
                        radial-gradient(ellipse var(--wash-width) var(--wash-height) at var(--light-x) var(--light-y),
                            rgba(255,255,255,var(--prism-white)) 0 8%,
                            rgba(255,181,93,0.13) 23%,
                            rgba(255,73,238,0.18) 39%,
                            rgba(151,92,255,0.16) 55%,
                            rgba(82,245,255,0.08) 70%,
                            transparent 86%),
                        radial-gradient(ellipse var(--band-width) var(--band-height) at var(--cyan-x) var(--cyan-y),
                            rgba(82,245,255,var(--prism-cyan)) 0 14px,
                            rgba(120,205,255,0.12) 44px,
                            transparent 118px),
                        radial-gradient(ellipse var(--band-width) var(--band-height) at var(--magenta-x) var(--magenta-y),
                            rgba(255,73,238,var(--prism-magenta)) 0 15px,
                            rgba(151,92,255,var(--prism-violet)) 40px,
                            rgba(255,185,98,0.16) 66px,
                            transparent 120px),
                        radial-gradient(ellipse 118px 66px at var(--light-x) var(--light-y),
                            rgba(255,255,255,var(--prism-white)) 0 5px,
                            rgba(255,255,255,0.08) 24px,
                            transparent 88px),
                        linear-gradient(135deg,
                            rgba(255,255,255,0.14) 0%,
                            rgba(0,255,255,0.1) 17%,
                            rgba(255,0,255,0.1) 35%,
                            transparent 56%,
                            rgba(255,255,255,0.1) 100%);
                    background-size: 100% 100%;
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: xor;
                    mask-composite: exclude;
                    opacity: var(--prism-opacity);
                    filter: saturate(var(--prism-saturation)) brightness(var(--prism-brightness));
                    pointer-events: none;
                    transition: opacity 120ms ease-out, filter 160ms ease-out;
                    will-change: opacity, filter;
                    z-index: 2;
                }

                @media (prefers-reduced-motion: reduce) {
                    .directional-glint::after {
                        transition: none;
                        filter: none;
                    }
                }
            `}</style>

            {rendererError && (
                <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999, background: 'rgba(255, 0, 0, 0.8)', color: 'white', padding: '10px', borderRadius: '4px', fontSize: '10px', pointerEvents: 'none' }}>
                    WebGL Blocked: {rendererError}
                </div>
            )}

            {/* Layer -1: Deep Void */}

            {/* Layer 0: The Particle Canvas */}
            <div ref={canvasContainerRef} className="absolute inset-0 z-10 pointer-events-none" />

            {/* Layer 2: The DOM Interrogation Page Wrapper */}
            <div className="relative z-20 w-full min-h-screen flex items-center justify-center bg-transparent pointer-events-none" style={{ isolation: 'isolate' }}>

                {/* Layer 3: The Crystal Card */}
                <div
                    ref={glassCardRef}
                    className="directional-glint relative z-20 w-full max-w-[420px] px-10 py-14 flex flex-col items-center pointer-events-auto"
                    style={{
                        '--light-x': '50%',
                        '--light-y': '0%',
                        '--cyan-x': 'calc(50% - 12px)',
                        '--cyan-y': '0%',
                        '--magenta-x': 'calc(50% + 12px)',
                        '--magenta-y': '0%',
                        '--band-width': '300px',
                        '--band-height': '120px',
                        '--wash-width': '380px',
                        '--wash-height': '168px',
                        '--prism-opacity': '0.42',
                        '--prism-white': '0.06',
                        '--prism-cyan': '0.04',
                        '--prism-magenta': '0.14',
                        '--prism-violet': '0.11',
                        '--prism-saturation': '1.02',
                        '--prism-brightness': '1.01',
                        backgroundColor: 'rgba(255, 255, 255, 0.005)',
                        backdropFilter: 'blur(20px) saturate(220%) brightness(1.2)',
                        WebkitBackdropFilter: 'blur(20px) saturate(220%) brightness(1.2)',
                        border: '1.5px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 30px 80px -15px rgba(0,0,0,0.8), inset 0 0 12px rgba(255, 255, 255, 0.05), inset 0 2px 4px rgba(255, 255, 255, 0.1)',
                        borderRadius: '2.5rem'
                    }}
                >
                    <div className="flex items-center justify-center w-full mb-10">
                        <img
                            src="/images/MJG_Logo.png"
                            alt="McJunkins Group"
                            className="h-16 w-auto brightness-0 invert transition-transform duration-300 hover:scale-105"
                        />
                    </div>

                    <div className="flex items-center w-full mb-10 gap-4">
                        <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                        <span className="text-[11px] text-white uppercase tracking-[0.15em] font-semibold opacity-60">Sign in</span>
                        <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                    </div>

                    {/* Layer 4: The Nested Button */}
                    <button
                        onClick={handleLogin}
                        className="relative overflow-hidden w-full flex items-center justify-center gap-3 py-3 px-6 text-[15px] font-medium text-white rounded-full border-t-[0.5px] border-white/30 transition-all duration-300 active:scale-[0.98] hover:bg-white/10 hover:scale-[1.02]"
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.8), 0 10px 20px rgba(0,0,0,0.4)',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E")`
                        }}
                    >
                        <svg className="w-5 h-5 relative z-10" viewBox="0 0 21 21" fill="none">
                            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                        </svg>
                        <span className="relative z-10">Continue with Microsoft</span>
                    </button>

                    <p className="mt-10 text-xs text-white/30 text-center">
                        Secure authentication powered by Microsoft Entra ID
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
